import {
  ApiError,
  Fetcher,
  FetcherInstance,
  FetcherOptions,
  FetcherResponse,
  FetcherResponseType,
  HTTPError,
} from '../models';
import { normalizeURL } from '../utils';

export function fetcher<T = any>(
  url: string,
  opts: FetcherOptions = {}
): FetcherInstance<T> {
  const getResponse = async () => {
    const {
      method = 'GET',
      json,
      headers,
      baseUrl,
      query,
      hooks = {},
      ...rest
    } = opts;

    const options: RequestInit = {
      method,
      headers,
      ...rest,
    };

    if (json) {
      options.headers = {
        'Content-Type': 'application/json',
        ...options.headers,
      };

      options.body = JSON.stringify(json);
    }

    const normalizedUrl =
      baseUrl !== undefined ? normalizeURL(url, baseUrl) : url;
    const searchParams = query
      ? new URLSearchParams(
          Object.entries(query).flatMap(([key, value]) =>
            Array.isArray(value) ? value.map((v) => [key, v]) : [[key, value]]
          )
        )
      : null;
    const fullUrl = searchParams
      ? `${normalizedUrl}?${searchParams.toString()}`
      : normalizedUrl;

    const request = new Request(fullUrl, options);

    if (hooks.before) {
      await hooks.before(request, opts);
    }

    const response = await fetch(request);
    const status = response.status;

    if (!response.ok) {
      if (response.headers.get('Content-Type') === 'application/json') {
        const body = await response.clone().json();

        throw new ApiError(response, body);
      }

      throw new HTTPError(response);
    }

    if (hooks.after) {
      await hooks.after(response, opts);
    }

    return {
      url: fullUrl,
      status,
      response,
    };
  };

  const getResponseTypeHandler =
    (key: FetcherResponseType) => async (): Promise<FetcherResponse<T>> => {
      const result = await getResponse();

      let data: T;
      if (
        result.status !== 204 &&
        result.response.headers.get('Content-Length') !== '0'
      ) {
        data = await result.response[key]();
      } else {
        data = null as unknown as T;
      }

      return {
        url: result.url,
        status: result.status,
        data,
        response: result.response,
      };
    };

  return {
    arrayBuffer: getResponseTypeHandler('arrayBuffer'),
    blob: getResponseTypeHandler('blob'),
    formData: getResponseTypeHandler('formData'),
    json: getResponseTypeHandler('json'),
    text: getResponseTypeHandler('text'),
    flush: () => getResponse(),
  };
}

export function createFetcher(defaultOptions: FetcherOptions): Fetcher {
  return function wrappedFetcher<T = any>(
    url: string,
    options: FetcherOptions = {}
  ) {
    return fetcher<T>(url, { ...defaultOptions, ...options });
  };
}
